import { computed, ref, type Ref } from 'vue'
import type { ItemRow, ItemSearchPage } from '~~/shared/items/itemRow'
import type { DocumentStatusFilter, DocumentTypeFilter } from '~~/shared/items/documentQuery'
import { pruneIds, type EntityCache } from '~/stores/entityCache'
import { SEARCH_PAGE_SIZE, type ApiFetch, type LoadState } from '~/composables/documents/browser'

export interface DocumentSearchDeps {
  apiFetch: ApiFetch
  cache: EntityCache<ItemRow>
  currentPath: Ref<string>
  typeFilters: Ref<DocumentTypeFilter[]>
  statusFilters: Ref<DocumentStatusFilter[]>
  /** Shared with the folder listing: both paint the same list, so they share its spinners. */
  loading: Ref<boolean>
  loadingMore: Ref<boolean>
  /** What an EMPTY query does instead of searching: re-read the folder listing. */
  fetchItems: () => Promise<void>
}

/** The finder's server-side search (AlexAmin/marcus#169, #440) — a ranked, pageable id list. */
export function useDocumentSearch(deps: DocumentSearchDeps) {
  const { apiFetch, cache, currentPath, typeFilters, statusFilters, loading, loadingMore } = deps

  const searchQuery = ref('')
  const searchIds = ref<string[]>([])
  /**
   * How many documents the SEARCH RANKING produced, which is not how many the library holds
   * (AlexAmin/marcus#169). `searchIds.length` is the page on screen; this is what it is a page OF,
   * so the finder can say "Mostrando 20 de 43" with the same words the folder listing already uses.
   */
  const searchTotal = ref(0)
  /**
   * Whether the last search ANSWERED, and how — not how many rows it found (AlexAmin/marcus#440).
   *
   * `searchTotal === 0` cannot tell the three states apart that the finder renders differently:
   * nothing searched yet, searched and matched nothing, searched and BROKE. It used to be the only
   * signal, because `searchItems` ended in a bare `catch {}`; a failed request therefore left the
   * store byte-identical to a successful empty one, and the page stated "Sin resultados" — a
   * positive claim about the library, manufactured out of an absence of information.
   *
   * The enum is `DeleteDocumentDialog.vue`'s, deliberately: it was fixed for the same defect (a
   * failed `/references` lookup read as "filed in no expediente") and the feature doc states the
   * rule in the general form — *una búsqueda fallida debe VERSE desconocida, no afirmar una lista
   * vacía*. An unknown must look unknown.
   */
  const searchState = ref<LoadState>('idle')
  const searchFailed = computed(() => searchState.value === 'failed')
  const searchResults = computed<ItemRow[]>(() => cache.resolve(searchIds.value))
  const hasMoreSearchResults = computed(() => searchIds.value.length < searchTotal.value)

  /** Forget the search entirely — a cleared box, or a navigation to another folder. */
  function resetSearch() {
    searchQuery.value = ''
    searchIds.value = []
    searchTotal.value = 0
    // The notice describes a request, and there is no longer a request for it to describe.
    searchState.value = 'idle'
  }

  /**
   * One page of search hits.
   *
   * `SEARCH_PAGE_SIZE`, not the server's default of 20: the default belongs to the AGENT, whose
   * prose answer never claimed to be exhaustive, while this page renders a list under a count and
   * therefore has to be able to say how much of the result it is showing (AlexAmin/marcus#169).
   *
   * `append` widens the SAME ranked list rather than paging a cursor — the ranking is recomputed per
   * request and there is no stable cursor over a fused score, so "Cargar más" asks for a bigger
   * slice of the same query and replaces what is on screen. That is why the ids are assigned, not
   * concatenated: concatenating a re-ranked superset would duplicate every row already loaded.
   */
  async function searchItems(query: string, options: { append?: boolean } = {}) {
    if (!query.trim()) {
      resetSearch()
      await deps.fetchItems()
      return
    }
    const append = options.append ?? false
    if (append) loadingMore.value = true
    else loading.value = true
    searchQuery.value = query
    searchState.value = 'loading'
    try {
      const limit = append ? searchIds.value.length + SEARCH_PAGE_SIZE : SEARCH_PAGE_SIZE
      const { items: rows, total } = await apiFetch<ItemSearchPage>('/api/search/items', {
        method: 'POST',
        // The lens goes into the RANKING, not over the rows it returns: `total` is the size of the
        // ranked set, so filtering afterwards would leave the count describing a longer list than
        // the one on screen (AlexAmin/marcus#169).
        body: {
          query, path: currentPath.value, limit,
          types: typeFilters.value.length ? typeFilters.value : undefined,
          statuses: statusFilters.value.length ? statusFilters.value : undefined,
        },
      })
      cache.upsert(rows)
      searchIds.value = rows.map(r => r.id)
      searchTotal.value = Math.max(total, rows.length)
      searchState.value = 'ready'
    } catch (err) {
      // THE COMMENT HERE USED TO READ "tracked by useApi" AND IT WAS NOT TRUE (#440).
      // `app/plugins/api.ts` re-throws everything and calls `recordFailure()` for a 503 ONLY, so a
      // 500 from this endpoint was recorded nowhere, surfaced nowhere, and logged nowhere. The
      // finder then rendered "Sin resultados" and the e2e suite saw a 30s timeout on a missing
      // element instead of an error — which is why #381/DET-DOCS-013 could not be told apart from a
      // genuine regression of the count line.
      searchState.value = 'failed'
      // Named, so a browser console or a retained Playwright trace says WHICH request died.
      console.error('[documents] /api/search/items failed', err)

      // THE ROWS GO, and this is the half that was worse than the "no results" half. `searchQuery`
      // is assigned BEFORE the request, so leaving `searchIds` alone left the PREVIOUS query's hits
      // on screen under the new query's search box, counted by `Mostrando N de M` — a successful
      // search for the wrong thing, with no signal at all that anything had failed.
      //
      // A failed `append` is the exception: those rows really are this query's, the widening is
      // what failed, and dropping them would throw away a good result to report a bad one.
      if (!append) {
        searchIds.value = []
        searchTotal.value = 0
      }
    } finally {
      loading.value = false
      loadingMore.value = false
    }
  }

  /** Widen the current search by one more page. No-op once the whole ranked set is on screen. */
  async function loadMoreSearchResults() {
    if (loadingMore.value || !hasMoreSearchResults.value) return
    await searchItems(searchQuery.value, { append: true })
  }

  /** Drop removed documents from the results. Part of the store's `remove()`. */
  function prune(gone: Set<string>) {
    searchIds.value = pruneIds(searchIds.value, gone)
  }

  return {
    searchQuery, searchResults, searchTotal, hasMoreSearchResults, searchState, searchFailed,
    searchItems, loadMoreSearchResults, resetSearch, prune,
  }
}
